// Copyright ©️ 2026 eVolve MEP, LLC

import { type Shipment, type ShipmentItem, useEvolvePatch } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { EntityStatusPoint } from 'components/Mantine/EntityStatusPoint';
import { GridMenu } from 'components/Mantine/GridMenu';
import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { useIsMobile } from 'hooks/useIsMobile';
import { useShipmentModals } from 'modules/Shop/Shipments/modalRegistries';
import { getRemainingItemsToShip, getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { WorkOrderItemTypeIcon } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemTypeIcon';

type Props = {
  shipment: Shipment;
  item: ShipmentItem;
  canEdit: boolean;
  onItemUpdated: () => void;
};

export const ShipmentItemRow = ({ shipment, item, canEdit, onItemUpdated }: Props) => {
  const isMobile = useIsMobile();
  const { uoms } = useUnitOfMeasure();
  const uomCode = uoms?.find((u) => u.unitOfMeasureId === item.unitOfMeasureId)?.code ?? '';

  const { apiCall: patchItemApiCall } = useEvolvePatch(`shop/shipment/${item.shipmentId}/item/${item.shipmentItemId}`);
  const maxQty = getRemainingItemsToShip(item) + item.allocatedQuantity;

  const { openModal } = useShipmentModals();

  return (
    <Flex align="center" justify="space-between" gap="sm" p="sm" bg="gray.1" style={{ borderRadius: 8 }}>
      <Flex align="center" gap="sm" flex={1}>
        <WorkOrderItemTypeIcon item={item} />
        <Stack gap={0} flex={1}>
          <Text fz="sm">{item.workOrderItemName}</Text>
          <Flex>
            <EditableCell
              Editor={({ onChange, value, onSubmit }) => (
                <NumberInputWrapped
                  label="Qty"
                  w={90}
                  min={1}
                  max={maxQty}
                  clampBehavior="strict"
                  onChange={onChange}
                  value={value}
                  onSubmit={onSubmit}
                  autoFocus
                  data-autofocus
                  allowDecimal={false}
                  description={`Max: ${formatQuantity(maxQty)}`}
                />
              )}
              editable={canEdit}
              initialValue={item.allocatedQuantity}
              required
              onSave={async (value) => {
                if (isNil(value)) return;
                await patchItemApiCall({ allocatedQuantity: value });
                onItemUpdated();
              }}
              className="transparent-editable-cell detail-page-item-thin"
              renderValue={
                <Text fz="sm" c="dimmed">
                  {formatQuantity(item.allocatedQuantity)} {uomCode}
                </Text>
              }
            />
          </Flex>
        </Stack>
      </Flex>
      <Flex align="center" gap="xs">
        {isMobile ? (
          <EntityStatusPoint customStatusEntityName="Task" stateName={getTaskStatusFromItem(item)} />
        ) : (
          <EntityStatusBadge customStatusEntityName="Task" stateName={getTaskStatusFromItem(item)} />
        )}
        {canEdit && (
          <GridMenu
            options={['Delete']}
            onMenuItemSelected={() => {
              openModal('RemoveItemModal', {
                shipment,
                deleteItem: item,
                onItemDeleted: onItemUpdated,
              });
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};
